import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { useAffiliateAuth } from '../../contexts/AffiliateAuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const AffiliateLogin = ({ onToggle }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login, resendVerification } = useAffiliateAuth();
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [pendingVerificationAffiliateId, setPendingVerificationAffiliateId] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});

  const localizeAffiliateLoginError = (message) => {
    if (!message) return message;
    const keyMap = {
      'Please verify your email before logging in. Check your email for a verification link.': 'affiliateAuth.error.emailNotVerified',
      'Your email is verified, but your affiliate account is pending admin approval.': 'affiliateAuth.error.pendingApproval',
      'Your affiliate account was rejected. Please contact support for details.': 'affiliateAuth.error.rejected',
      'Your affiliate account is suspended. Please contact support.': 'affiliateAuth.error.suspended',
      'Affiliate account is suspended. Please contact support.': 'affiliateAuth.error.suspended',
      'Your affiliate account is not active. Please contact support.': 'affiliateAuth.error.notActive'
    };
    const key = keyMap[message];
    return key ? t(key) : message;
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return t('auth.validationEmailRequired');
    if (!emailRegex.test(email)) return t('auth.validationEmailInvalid');
    return null;
  };

  const validatePassword = (password) => {
    if (!password) return t('auth.validationPasswordRequired');
    if (password.length < 6) return t('auth.validationPasswordMin');
    return null;
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        return validateEmail(value);
      case 'password':
        return validatePassword(value);
      default:
        return null;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation
    const error = validateField(name, value);
    setValidationErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfoMessage('');
    setPendingVerificationAffiliateId('');

    // Mark all fields as touched
    setTouched({ email: true, password: true });

    // Validate all fields
    const emailError = validateEmail(formData.email);
    const passwordError = validatePassword(formData.password);
    
    if (emailError || passwordError) {
      setValidationErrors({
        email: emailError,
        password: passwordError
      });
      setLoading(false);
      return;
    }

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        console.log('Affiliate login successful');
        navigate('/affiliate/dashboard');
      } else {
        console.log('Affiliate login failed:', result.error);
        if (result.requiresVerification) {
          setError(localizeAffiliateLoginError(result.error || t('auth.emailNotVerified')));
          setPendingVerificationAffiliateId(result.affiliateId || '');
        } else {
          setError(localizeAffiliateLoginError(result.error));
        }
      }
    } catch (err) {
      console.error('Affiliate login error:', err);
      setError(t('auth.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!pendingVerificationAffiliateId) return;
    setResendLoading(true);
    setInfoMessage('');
    try {
      const result = await resendVerification(pendingVerificationAffiliateId);
      if (result.success) {
        setInfoMessage(result.message || t('auth.verificationEmailResent'));
      } else {
        setError(result.error || t('auth.resendVerificationFailed'));
      }
    } finally {
      setResendLoading(false);
    }
  };

  const isFormValid = !validationErrors.email && !validationErrors.password && formData.email && formData.password;

  return (
    <div className={`min-h-screen relative overflow-hidden ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob" style={{ backgroundColor: 'var(--chart-1)' }}></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000" style={{ backgroundColor: 'var(--chart-2)' }}></div>
        <div className="absolute top-40 left-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000" style={{ backgroundColor: 'var(--chart-3)' }}></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* Logo above the form */}
          <div className="text-center -mt-24">
            <img 
              src={isDark ? "/images/Logo-dark.png" : "/images/Logo-light.png"} 
              alt="Logo"
              className="w-48 h-48 object-contain mx-auto"
            />
          </div>
          <div className="glass-card">
            {/* Header */}
            <div className={`text-center mb-8 ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
              <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {t('affiliateAuth.signIn')}
              </h1>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {t('affiliateAuth.signInDescription')}
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className={`mb-6 p-4 rounded-xl flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`} style={{ 
                background: 'rgba(239, 68, 68, 0.1)', 
                border: '1px solid rgba(239, 68, 68, 0.2)',
                color: 'var(--error-color)'
              }}>
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            {infoMessage && (
              <div className={`mb-6 p-4 rounded-xl flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`} style={{
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.2)',
                color: 'var(--success-color)'
              }}>
                <CheckCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{infoMessage}</span>
              </div>
            )}
            {pendingVerificationAffiliateId && (
              <button
                type="button"
                className="glass-button-outline w-full mb-6 py-3 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleResendVerification}
                disabled={resendLoading}
              >
                {resendLoading ? t('auth.sending') : t('auth.resendVerificationEmail')}
              </button>
            )}

            <form onSubmit={handleSubmit} className={`space-y-6 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
              {/* Email field */}
              <div>
                <label htmlFor="affiliate-email" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <input
                    id="affiliate-email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`glass-input ${i18n.language === 'ar' ? 'pl-10 text-right' : 'pr-10 text-left'} ${touched.email && validationErrors.email ? 'border-red-400' : ''}`}
                    placeholder={t('auth.emailPlaceholder')}
                    aria-describedby={touched.email && validationErrors.email ? 'affiliate-email-error' : undefined}
                    aria-invalid={touched.email && validationErrors.email ? 'true' : 'false'}
                    dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                  />
                  {touched.email && !validationErrors.email && formData.email && (
                    <CheckCircle className={`absolute top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500 ${i18n.language === 'ar' ? 'left-3' : 'right-3'}`} />
                  )}
                </div>
                {touched.email && validationErrors.email && (
                  <p id="affiliate-email-error" className={`mt-2 text-sm flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-1' : 'space-x-1'}`} style={{ color: 'var(--error-color)' }}>
                    <AlertCircle className="h-4 w-4" />
                    <span>{validationErrors.email}</span>
                  </p>
                )}
              </div>

              {/* Password field */}
              <div>
                <label htmlFor="affiliate-password" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <input
                    id="affiliate-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`glass-input ${i18n.language === 'ar' ? 'pl-20 text-right' : 'pr-20 text-left'} ${touched.password && validationErrors.password ? 'border-red-400' : ''}`}
                    placeholder={t('auth.passwordPlaceholder')}
                    aria-describedby={touched.password && validationErrors.password ? 'affiliate-password-error' : undefined}
                    aria-invalid={touched.password && validationErrors.password ? 'true' : 'false'}
                    dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                  />
                  <div className={`absolute top-1/2 transform -translate-y-1/2 flex items-center ${i18n.language === 'ar' ? 'left-3 space-x-reverse space-x-2' : 'right-3 space-x-2'}`}>
                    {touched.password && !validationErrors.password && formData.password && (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    )}
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                {touched.password && validationErrors.password && (
                  <p id="affiliate-password-error" className={`mt-2 text-sm flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-1' : 'space-x-1'}`} style={{ color: 'var(--error-color)' }}>
                    <AlertCircle className="h-4 w-4" />
                    <span>{validationErrors.password}</span>
                  </p>
                )}
              </div>

              <div className={`${i18n.language === 'ar' ? 'text-left' : 'text-right'} -mt-2`}>
                <Link
                  to="/affiliate/forgot-password"
                  className="text-sm font-medium hover:underline"
                  style={{ color: 'var(--accent-primary)' }}
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading || !isFormValid}
                className="glass-button w-full py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                aria-describedby={loading ? 'loading-message' : undefined}
              >
                {loading ? (
                  <div className={`flex items-center justify-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>{t('auth.signingIn')}</span>
                  </div>
                ) : (
                  t('auth.signIn')
                )}
              </button>

              {/* Loading message for screen readers */}
              {loading && (
                <div id="loading-message" className="sr-only">
                  {t('auth.loadingSignIn')}
                </div>
              )}
            </form>

            {/* Footer */}
            <div className={`mt-8 text-center ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {i18n.language === 'ar' ? (
                  <>
                    <button
                      onClick={onToggle}
                      className="font-medium hover:underline transition-colors"
                      style={{ color: 'var(--accent-primary)' }}
                    >
                      {t('auth.createAccount')}
                    </button>
                    {' '}{t('affiliateAuth.noAccount')}
                  </>
                ) : (
                  <>
                    {t('affiliateAuth.noAccount')}{' '}
                    <button
                      onClick={onToggle}
                      className="font-medium hover:underline transition-colors"
                      style={{ color: 'var(--accent-primary)' }}
                    >
                      {t('auth.createAccount')}
                    </button>
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateLogin;

